import {
  Badge,
  BadgeProps,
  Grid,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import { SubscriptionPlanTypes } from '../../types/coach';
import Plan from './Plan';
import { CLIENTS_ROUTE } from '../../utils/routes';
import { useAppSelector } from '../../redux/hooks';
import {
  activateFreePlanSuccess,
  clearClientSecret,
  createCheckoutSession,
} from '../../redux/subscription/subscriptionSlice';
import Checkout from './Checkout';
import PaymentInfo from './PaymentInfo';
import SubscriptionSuccess from '../SubscriptionSuccess/SubscriptionSuccess';
import { getPlans } from '../../utils/paymentUtills';
import {
  ENABLE_FREE_PLAN,
  TEnableFreePlanReq,
  TEnableFreePlanRes,
} from '../../service/graphql/subscriptionQuery';
import { toastStyles } from '../../utils/utils';
import { fetchCoachRequest } from '../../redux/coach/coachSlice';
import { onboardingActions } from '../../redux/onboarding/onboardingSlice';

export const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: 10,
    top: 10,
  },
}));

export default function SubscriptionPlan() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clientSecret, createCheckoutSessionLoading } = useAppSelector(
    (state) => state.subscription
  );
  const { id: coachId, isPrincipalCoach } = useAppSelector((state) => state.coach.coach);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const sessionId = urlParams.get('session_id');
  const [loadingPlan, setLoadingPlan] = useState<SubscriptionPlanTypes | null>(null);
  const [moreInfoPlan, setMoreInfoPlan] = useState<SubscriptionPlanTypes | null>(null);

  const [enableFreePlan, { data, error, loading, called }] = useMutation<
    TEnableFreePlanRes,
    TEnableFreePlanReq
  >(ENABLE_FREE_PLAN);

  const plans = Object.values(getPlans()).map((plan) => ({
    planIcon: `/assets/icons/${plan.planType.toLowerCase()}.svg`,
    planType: plan.planType,
    price: plan.price,
    features: plan.features,
  }));

  const onPurchase = (plan: SubscriptionPlanTypes) => {
    setLoadingPlan(plan);
    if (plan === SubscriptionPlanTypes.FREE) {
      enableFreePlan({ variables: { coachId } });
    } else {
      dispatch(
        createCheckoutSession({
          coachId,
          planType: plan,
        })
      );
      dispatch(onboardingActions.openOnboardingSuccessModal());
    }
  };

  useEffect(() => {
    if (!called || loading) return;
    if (!data?.activateFreePlan?.success || error) {
      const errMsg =
        data?.activateFreePlan?.message ?? error?.message ?? t('settings.requestError');
      toast.error(errMsg, toastStyles.error);
      return;
    }
    if (data?.activateFreePlan?.success && data?.activateFreePlan.message) {
      toast.success(data?.activateFreePlan?.message, toastStyles.success);
      dispatch(activateFreePlanSuccess());
      dispatch(onboardingActions.openOnboardingSuccessModal());
      dispatch(fetchCoachRequest());
      navigate(CLIENTS_ROUTE);
    }
  }, [data, error]);

  useEffect(() => {
    dispatch(clearClientSecret());
  }, []);

  if (!plans) {
    return null;
  }

  if (!isPrincipalCoach) {
    return null;
  }

  if (clientSecret) {
    return <Checkout clientSecret={clientSecret} showBackButton />;
  }

  if (moreInfoPlan) {
    return <PaymentInfo plan={moreInfoPlan} dismiss={() => setMoreInfoPlan(null)} />;
  }

  if (sessionId) {
    return <SubscriptionSuccess />;
  }

  return (
    <Grid container flexDirection="column" alignItems="center" mt={isSmallScreen ? 6 : 0}>
      <Grid item textAlign="center" px={isSmallScreen ? 1 : 0} maxWidth={615}>
        <Typography variant={isSmallScreen ? 'h6' : 'title1Bold'} color="primary.main">
          {t('therapistOnboarding.selectSubscriptionPlan.title')}
        </Typography>
      </Grid>
      <Grid
        container
        pt={9}
        display="flex"
        flexDirection="row"
        justifyContent="center"
        px={1}
        gap={1}
      >
        {plans.map((plan) => (
          <Plan
            key={plan.planType}
            planIcon={plan.planIcon}
            planType={plan.planType}
            price={plan.price}
            features={plan.features}
            handleSelectPlan={onPurchase}
            showMoreInfo={setMoreInfoPlan}
            xs={12}
            sm={5}
            md={5}
            lg={2.9}
            loading={loadingPlan === plan.planType && (createCheckoutSessionLoading || loading)}
          />
        ))}
      </Grid>
      <Grid width="100%" p="36px 2px" display="flex">
        <Typography
          variant="bodyRegular"
          fontWeight={500}
          color="primary"
          textAlign="center"
          width="100%"
        >
          {t('therapistOnboarding.selectSubscriptionPlan.changeLater')}
        </Typography>
      </Grid>
    </Grid>
  );
}
